// The match as a list of steps the player can look back at. A step is a
// state in which the board or the rules differ from the state before it: a
// move, or a spell that changed something. Pure functions. The store in
// src/state/history.ts holds the list and the cursor.

import { spellById } from '../spells/library';
import type { SpellPolarity } from '../spells/types';
import type { GameState } from './state';

export type HistoryStep = {
  state: GameState;
  /** The log line that says what the step did. */
  line: string;
  /** Squares a spell changed in this step. Empty for a move. */
  squares: readonly number[];
  /** Colors the marks on those squares. Null when no spell made the step. */
  tone: SpellPolarity | null;
};

const START_LINE = 'The start of the match.';

export const firstStep = (state: GameState): HistoryStep => ({
  state,
  line: START_LINE,
  squares: [],
  tone: null,
});

/** The step an action made, or null when the action left the board and the rules alone. */
export const stepBetween = (before: GameState, after: GameState): HistoryStep | null => {
  if (after === before) return null;
  const ended = after.phase === 'over' && before.phase !== 'over';
  if (after.board === before.board && after.rules === before.rules && !ended) return null;

  const cast = after.lastCast !== before.lastCast ? after.lastCast : null;
  const firstNewLine = after.log.find((entry) => entry.id >= before.nextLogId)?.text ?? '';
  return {
    state: after,
    line: cast?.line || firstNewLine,
    squares: cast?.squares ?? [],
    tone: cast ? spellById(cast.spellId).polarity : null,
  };
};

/**
 * The cursor for a requested step. Null is the live game. A request past the
 * last step is live. So is the last step itself, unless the live game moved
 * on from it with an action that made no step (a roll that waits for an aim).
 */
export const resolveCursor = (
  requested: number,
  stepCount: number,
  liveIsAhead: boolean,
): number | null => {
  const lastStep = stepCount - 1;
  if (requested > lastStep) return null;
  if (requested === lastStep && !liveIsAhead) return null;
  return Math.max(0, requested);
};

/** The step one back from the cursor. From the live game, the newest step that differs from it. */
export const stepBefore = (
  cursor: number | null,
  stepCount: number,
  liveIsAhead: boolean,
): number => {
  const from = cursor ?? (liveIsAhead ? stepCount : stepCount - 1);
  return Math.max(0, from - 1);
};

/** The first step whose state holds the log entry. Past the last step when none does. */
export const stepWithLogEntry = (steps: readonly HistoryStep[], logId: number): number => {
  const index = steps.findIndex((step) => step.state.nextLogId > logId);
  return index === -1 ? steps.length : index;
};
